import React, { useState, useEffect, useContext, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { Loading } from "app/components/Wrappers";
import PagePanel from "app/components/PagePanel";
import PendingPurchasesList from "app/components/Programs/PendingPurchasesList";

import { useEventsModel } from "./indexModel";

import styles from "./styles.module.scss";

import NoSession from "./NoSession";
import { schedulingFetchDelete, schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import SessionCard from "./SessionCard";
import InfoModal from "app/routes/Calendar/InfoModal";
import PurchaseSuccessModal from "app/routes/CoachProfileInfo/PurchaseSuccessModal";
import { removeEvents } from "app/actions/events";
import { EventType } from "app/models/EventModel";

import {
  initBookSession,
  resetBookSession,
  setBookSession,
} from "app/actions/scheduling/bookSession";
import { UserContext } from "app/contexts/UserContext";
import { toast } from "react-toastify";
import { useActions } from "app/utils/hooks";
import { fetchSelf } from "app/actions/user";
import { RootState } from "typedefs";
import { BookSessionState } from "app/reducers/scheduling/bookSession";

import CalendarIco from "../../../public/images/calendar-ico.svg";
import ClockIco from "../../../public/images/clock-ico.svg";
import LocationIco from "../../../public/images/location.svg";
import i18n from "app/i18n";
import ButtonVariant from "app/components/Button/ButtonVariant";
import ProgramCard from "app/components/PublicProfiles/Facility/ProgramCard";
import { transformProgramToEvent } from "app/components/PublicProfiles/services/api";
import { getCoachAvatar } from "app/components/PublicProfiles/Facility/FacilityPage";
import {CreditBundle} from "app/models/scheduling/CreditBundleModel";
import {transformCreditBundlesToOfferings} from "app/utils/creditBundles";
import Subscription from "app/routes/Billing/NewOffering/Subscription";
import SubscriptionCardWithCoach from "app/routes/Billing/Customers/CustomerDetails/SubscriptionCardWithCoach";
import ParticipantPickerModal from "./ParticipantPickerModal";
import ViewingChip, { ViewingOption } from "./ViewingChip";
import { useBookingParticipants } from "./useBookingParticipants";

type ConfirmationDialogData = {
  show?: boolean;
  title?: string;
  status?: "success" | "error";
  content?: string;
  paymentMethod?: "online" | "package" | string;
};

interface RateLink {
  title: string;
  quantity: number;
  duration: string;
  remainingSessions: number;
  unlimited: boolean;
}

interface Subscription {
  fullName?: string;
  profilePicture: string;
  offeringTitle: string;
  billingFrequency: number | null;
  nextInvoiceDate: string | null;
  expiredAt?: string | null;
  productPrice: number;
  currency: string;
  remainingSessions: number | null;
  unlimited?: boolean;
  productType?: string;
  productId?: string;
  coachId?: string;
  studentProfileId?: string;
  rateLinks?: RateLink[];
  cancelled?: boolean;
}

export default function Sessions() {
  const dispatch = useDispatch();
  const user = useContext(UserContext);
  const history = useHistory();
  const { t } = useTranslation();
  const userAction = useActions({ fetchSelf });
  const bookSession = useSelector(
    (state: RootState): BookSessionState => state.bookSession,
  );
  const selfUser = useSelector((state: RootState) => state.user?.self);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [showSessionBookedModal, setShowSessionBookedModal] = useState(params.get("session_booked_modal") === "true");
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [columnCount, setColumnCount] = useState(4);
  const [programs, setPrograms] = useState({ active: [], completed: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
  const [showPurchaseSuccessModal, setShowPurchaseSuccessModal] = useState(params.get("payment") === "success");
  const [purchasedProductTitle] = useState(params.get("title") || "");
  const [isSubscription] = useState(params.get("offering_type") === "subscription");
  const [confirmationDialog, setConfirmationDialog] =
    useState<ConfirmationDialogData>({});

  // CN-1323 (P7): a family head can view bookings + packages for themselves, a
  // single dependent, or the whole family. `viewing` is "self", "all", or a
  // member's external id. Roster comes from CoachNow.API; an athlete with no
  // managed members never sees the selector and the page is unchanged.
  const { familyMembers, candidates, refetch } = useBookingParticipants();
  const [viewing, setViewing] = useState<string>("self");
  // CN-1323: the "Who is this session for?" picker, opened from Book.
  const [showParticipantPicker, setShowParticipantPicker] = useState(false);

  const eventsFilter =
    viewing === "self"
      ? {}
      : viewing === "all"
        ? { includeFamily: true }
        : { participantAthleteId: viewing };

  const { events } = useEventsModel(
    DateTime.now(),
    DateTime.now().plus({ years: 1 }),
    eventsFilter,
  );

  useEffect(() => {
    const updateColumnCount = () => {
      const width = window.innerWidth;
      if (width <= 480) { // sm
        setColumnCount(1);
      } else if (width <= 1020) { // lg
        setColumnCount(2);
      } else if (width <= 1400) { // xxl
        setColumnCount(3);
      } else {
        setColumnCount(4);
      }
    };

    updateColumnCount();
    window.addEventListener("resize", updateColumnCount);
    return () => window.removeEventListener("resize", updateColumnCount);
  }, []);

  useEffect(() => {
    if (params.get("session_booked_modal") !== "true") {
      dispatch(resetBookSession());
    }
  }, []);

  useEffect(() => {
    setIsLoadingSubscriptions(true);

    // CN-1323 (P7): packages follow the same `viewing` selector as bookings.
    // student_id scopes to a dependent (authorized by head_of? on the BE);
    // "all" unions the head + every managed member.
    const fetchBundles = (studentId?: string) =>
      schedulingFetchGet<CreditBundle[]>(
        "/credit_bundles",
        studentId ? { student_id: studentId } : {},
      );

    const request =
      viewing === "all"
        ? Promise.all([
            fetchBundles(),
            ...familyMembers.map((m) => fetchBundles(m.userId)),
          ]).then((lists) => lists.flat())
        : fetchBundles(viewing === "self" ? undefined : viewing);

    request
      .then((response) => {
        if (!response) {
          throw new Error("Unexpected response format");
        }
        const offerings = transformCreditBundlesToOfferings(response);
        // Transform to Subscription format with required profilePicture
        const subscriptionsData = offerings.map((o) => ({
          ...o,
          profilePicture: o.profilePicture || "",
          remainingSessions: o.remainingSessions as number | null,
        }));
        setSubscriptions(subscriptionsData);
      })
      .catch((err) => {
        console.error("Failed to fetch subscriptions:", err);
      })
      .finally(() => {
        setIsLoadingSubscriptions(false);
      });
  }, [viewing, familyMembers]);

  const fetchPrograms = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      // CN-2566: scope the registered-programs list to the selected member so a
      // child's page doesn't show the whole family's programs. Self and each
      // child send participant_athlete_id (the member's external id); "All
      // Family" omits it so the backend returns the whole household.
      const programsParams: { status: string; participant_athlete_id?: string } = { status: "active" };
      if (viewing === "self") {
        if (selfUser?.id) {
          programsParams.participant_athlete_id = selfUser.id;
        }
      } else if (viewing !== "all") {
        programsParams.participant_athlete_id = viewing;
      }
      // version 3: v2 strips recurring clinics (attendance=clinic + recurrence_group),
      // so registered recurring clinics never reached this section (CN-2566).
      const response = await schedulingFetchGet("/programs", programsParams, { version: 3 }, signal, false);
      setPrograms(response.data);
    } catch (err) {
      // A stale request aborted by a newer `viewing` selection rejects with a
      // 499 (see responseHandler.ts) — ignore it so it can't clobber the
      // state of the request that superseded it.
      if (err?.status === 499 || signal?.aborted) {
        return;
      }
      setError(err);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [viewing, selfUser?.id]);

  useEffect(() => {
    const controller = new AbortController();
    fetchPrograms(controller.signal);
    return () => controller.abort();
  }, [fetchPrograms]);

  // Clean up payment success URL params after modal is shown
  useEffect(() => {
    if (showPurchaseSuccessModal) {
      const newParams = new URLSearchParams(location.search);
      newParams.delete("payment");
      newParams.delete("title");
      newParams.delete("offering_type");
      history.replace({
        pathname: location.pathname,
        search: newParams.toString(),
      });
    }
  }, [showPurchaseSuccessModal]);

  if (user.kind !== "athlete" && user.kind !== "other") {
    toast.error(t("Only athletes can access this resource."));
    history.push("/");
    return null;
  }

  const sessions = events.data.filter((item) => item.type !== "program");
  const myPrograms = programs?.active?.filter(
    // CN-1323 (CFTB-3343): in the "All Family" view, also keep a program when a
    // managed dependent is registered (familyMemberRegistered), so a parent sees
    // their child's registration — including private/restricted programs they
    // aren't part of themselves. For a single member (self or a child) the list
    // is already scoped server-side (CN-2566), so filter purely on that member's
    // own status — otherwise every member's page shows the whole family's programs.
    (p) =>
      p.registrationStatus === "Registered" ||
      p.registrationStatus === "Waitlisted" ||
      p.registrationStatus === "Pending Payment" ||
      (viewing === "all" && p.familyMemberRegistered)
  ) || [];

  if (events.pending) {
    return <Loading isLoading loadType="spinner" />;
  }

  const onEditSession = (session) => {
    const startHour = DateTime.fromISO(session.start).setZone(session.timeZone);
    const endHour = DateTime.fromISO(session.end).setZone(session.timeZone);

    dispatch(
      initBookSession({
        ...session,
        selectedSession: session,
        time: {
          startHour: startHour.toMillis(),
          endHour: endHour.toMillis(),
          timeZone: session.timeZone,
          coachId: session.coachProfile.id,
        },
      }),
    );
    history.push("/sessions/edit");
  };

  const onCancelSession = (session, closeModal) => {
    // Check if session is non-refundable
    const isNonRefundable = session.refundable === false;

    let body = {};
    let showRefundLikeDialog = false;
    let effectivePaymentMethod = session.payment_method;

    if (isNonRefundable) {
      // Non-refundable session - send refund=false
      body = { refund: false };
      showRefundLikeDialog = false;
    } else {
      // Use existing refund logic for refundable sessions
      const isOnline = session.payment_method === "online";
      const isPackage = session.payment_method === "package";
      const rateNum = Number(session?.rate ?? 0);
      const isZeroPricedOnline = isOnline && (!Number.isFinite(rateNum) || rateNum <= 0);
      const shouldRefund = (!isZeroPricedOnline && isOnline) || isPackage;
      body = shouldRefund ? { refund: true } : {};

      const isPending = session?.payment_status === 'pending';
      showRefundLikeDialog = !isPending && shouldRefund;
      effectivePaymentMethod = isZeroPricedOnline ? 'offline' : session.payment_method;
    }

    // Returned so the caller can keep its confirm button disabled until the request
    // settles: this takes ~2s on the refund path and every extra click was another DELETE.
    return schedulingFetchDelete(`bookings/${session.id}`, body)
      .then(() => {
        if (isNonRefundable) {
          // Show non-refundable message
          setConfirmationDialog({
            title: t('Session Canceled'),
            content: t('This session has been successfully canceled. Please contact your coach for more details about the refund as it couldn\'t be issued automatically.'),
            status: "success",
            show: true,
            paymentMethod: effectivePaymentMethod,
          });
        } else {
          // Show existing refund messages
          const isOnline = session.payment_method === "online";
          const refundContent = isOnline
            ? t("You will get a {{amount}} refund for this session that was billed online.", {
              amount: `${session?.coachProfile?.currencySymbol ?? ""}${session?.rate}`,
            })
            : t(
              'You will receive credit for the session in the {{pkg}} {{type}} that was billed as a payment option.',
              { pkg: session?.productName, type: (session.productType === 'product' ? 'package' : 'subscription') }
            );

          const refundTitle = isOnline ? t("Session refund") : t("Credit refund");

          setConfirmationDialog({
            title: showRefundLikeDialog ? refundTitle : t('Session Canceled'),
            content: showRefundLikeDialog ? refundContent : t('This session has been successfully canceled.'),
            status: "success",
            show: true,
            paymentMethod: effectivePaymentMethod,
          });
        }

        dispatch(removeEvents([session.id], EventType.LESSON_SCHEDULE));
        closeModal();
      })
      .catch((errors) => {
        closeModal();
        setConfirmationDialog({
          title: t("Could not cancel session"),
          content: errors.errors[0].details[0].message,
          status: "error",
          show: true,
        });
      });
  };

  const handleCalendarSettings = () => {
    history.push("/calendar/integration");
  };

  const handleNewSession = () => {
    // With managed family members, pick the participant first; otherwise this is
    // a plain self-booking and we go straight into the availability flow.
    if (familyMembers.length > 0) {
      setShowParticipantPicker(true);
    } else {
      history.push("/sessions/session_availability");
    }
  };

  const handleConfirmParticipant = (participantAthleteId: string | null) => {
    dispatch(setBookSession({ participantAthleteId }));
    setShowParticipantPicker(false);
    history.push("/sessions/session_availability");
  };

  const bookingSessionSuccess = () => {
    userAction.fetchSelf();
    dispatch(resetBookSession());
    setShowSessionBookedModal(false);
    history.replace("/sessions");
  };

  const formattedDate = () => {
    if (!bookSession.date) {
      return '';
    }

    const timeZone = bookSession.time?.timeZone;

    if (timeZone) {
      return DateTime.fromISO(bookSession.date)
        .setZone(timeZone)
        .toLocaleString({ year: 'numeric', month: 'long', day: 'numeric' }, { locale: i18n.language });
    }

    const date = new Date(bookSession.date);
    return date.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formattedTime = () => {
    if (!bookSession.time) {
      return '';
    }

    return DateTime.fromMillis(bookSession.time.startHour).setZone(bookSession.time.timeZone).toFormat('hh:mm a ZZZZ');
  };

  const renderProgramList = () => {
    if (loading) {
      return <Loading isLoading loadType="spinner" />;
    }

    if (error) {
      return <div>{t("Failed to load programs")}</div>;
    }

    if (myPrograms.length === 0) {
      return <div>{t("No upcoming events or clinics")}</div>;
    }

    const displayedPrograms = showAllPrograms ? myPrograms : myPrograms.slice(0, columnCount);

    return (
      <div className={styles.pub_facility_events_grid}>
        {displayedPrograms?.map((program) => {
          const transformedEvent = transformProgramToEvent(program, program.facilityName, program.coaches);
          const facility = { name: program.facilityName };

          return (
            <ProgramCard
              key={program.id}
              event={transformedEvent}
              facility={facility}
              getCoachAvatar={getCoachAvatar}
              onClickRegister={(p) => {
                history.push(`/public/programs/${p.id}/view`);
              }}
              alreadyRegistered={true}
              program={program}
            />
          );
        })}
      </div>
    );
  };

  const formatInvoiceDate = (isoDate: string | null) => {
    if (!isoDate) {
      return t("N/A");
    }
    const date = new Date(isoDate);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const handleDelete = (productId: string) => {
    setSubscriptions((prevSubscriptions) =>
      prevSubscriptions.map((subscription) =>
        subscription.productId === productId
          ? { ...subscription, nextInvoiceDate: null, cancelled: true }
          : subscription,
      ),
    );
  };

  // Get coach display info, handling multiple data formats
  const getCoachDisplayInfo = () => {
    const coach = bookSession.coach;
    if (!coach) {
      return null;
    }

    // Try fullName first (from CoachProfileModel or our fix)
    let name = coach.fullName;

    // Fall back to firstName + lastName if fullName is missing
    if (!name && (coach.firstName || coach.lastName)) {
      name = [coach.firstName, coach.lastName].filter(Boolean).join(' ');
    }

    // Skip if no name at all or it's the "All Coaches" placeholder
    if (!name || coach.id === 'all-coaches') {
      return null;
    }

    return {
      name,
      profilePicture: coach.profilePicture,
    };
  };

  const coachInfo = getCoachDisplayInfo();

  const viewingOptions: ViewingOption[] = [
    {
      value: "self",
      label: t("My Sessions"),
      avatar: (selfUser?.avatar as string) ?? null,
    },
    ...familyMembers.map((m) => ({
      value: m.userId,
      label: `${m.firstName} ${m.lastName}`.trim(),
      avatar: m.avatar,
    })),
    { value: "all", label: t("All Family"), avatar: null },
  ];

  return (
    <div className={`${styles.container} sessions-page-container`}>
      <ParticipantPickerModal
        isOpen={showParticipantPicker}
        candidates={candidates}
        onClose={() => setShowParticipantPicker(false)}
        onConfirm={handleConfirmParticipant}
        onMemberAdded={refetch}
        defaultParticipantId={viewing === "self" || viewing === "all" ? null : viewing}
      />
      <PagePanel
        title={t("Bookings")}
        url="/sessions/new"
        titleStyles={styles.page_panel_title}
        headerChildren={
          <div className={styles.page_panel_children}>
            {familyMembers.length > 0 && (
              <ViewingChip
                options={viewingOptions}
                value={viewing}
                onChange={setViewing}
              />
            )}
            <ButtonVariant
              title={t("Book")}
              buttonType="secondary-filled"
              onClickFunc={handleNewSession}
              icon={<i className="ico ico-add" />}
              childStyles={styles.book_button_icon}
              buttonStyles={styles.book_button}
              data-testid="sessions-book-button"
            />
            <ButtonVariant
              title={''}
              buttonType="secondary-filled"
              onClickFunc={handleCalendarSettings}
              icon={<i className="ico ico-cog" />}
              childStyles={styles.settings_button_icon}
              buttonStyles={styles.settings_button}
            />
          </div>
        }
        panelClassName={styles.page_panel_container}
      />
      <div className={styles.section_header}>
        <h3 className={styles.subtitle}>{t("Upcoming Sessions")}</h3>
        {sessions.length > columnCount && (
          <div
            className={styles.view_all_link}
            onClick={() => setShowAllSessions(!showAllSessions)}
          >
            {showAllSessions ? t("Hide") : t("View All")}
          </div>
        )}
      </div>
      <PendingPurchasesList filterByPurchasableType="TimeSlotReservation" />
      <div className={`${sessions.length > 0 ? styles.session_cards : ""} `}>
        {sessions.length > 0 ? (
          (showAllSessions ? sessions : sessions.slice(0, columnCount)).map((session, index) => (
            <SessionCard
              key={session.id}
              session={session}
              first={index === 0}
              onEditSession={onEditSession}
              onCancelSession={onCancelSession}
            />
          ))
        ) : (
          <NoSession />
        )}
      </div>

      {(myPrograms.length > 0 || error) && (
        <div className={styles.pub_facility_section}>
          <div className={styles.section_header}>
            <h3 className={styles.subtitle}>{t("Upcoming Events and Clinics")}</h3>
            {myPrograms.length > columnCount && (
              <div
                className={styles.view_all_link}
                onClick={() => setShowAllPrograms(!showAllPrograms)}
              >
                {showAllPrograms ? t("Hide") : t("View All")}
              </div>
            )}
          </div>
          {renderProgramList()}
        </div>
      )}

      {!isLoadingSubscriptions && subscriptions.length > 0 &&
        <div className={styles.pub_facility_section}>
          <div className={styles.section_header}>
            <h3 className={styles.subtitle}>{t("Active Packages & Subscriptions")}</h3>
          </div>
          <div className={styles.subscriptionMargin}>
            {subscriptions.map((subscription, index) => (
              <SubscriptionCardWithCoach
                key={index}
                subscription={subscription}
                coachName={subscription?.fullName || ""}
                coachImage={subscription.profilePicture}
                formatInvoiceDate={formatInvoiceDate}
                handleDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      }

      {confirmationDialog.show && (
        <InfoModal
          isOpen
          title={confirmationDialog.title}
          status={confirmationDialog.status}
          onDismiss={() => {
            setConfirmationDialog({});
          }}
          paymentMethod={confirmationDialog.paymentMethod}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <div>{confirmationDialog.content}</div>
          </div>
        </InfoModal>
      )}

      <InfoModal
        isOpen={showSessionBookedModal}
        title={t("Session Booked")}
        status="success"
        onDismiss={() => {
          bookingSessionSuccess();
        }}
      >
        <div className={styles.booked_modal}>
          <div className={styles.info_text}>{t("You have successfully booked a new session")}</div>
          {coachInfo && <div className={styles.coach_info_modal}>
            {coachInfo.profilePicture && <img className={styles.coach_image} src={coachInfo.profilePicture} />}
            <div className={styles.coach_name}>{coachInfo.name}</div>
          </div>}
          <div className={styles.details_modal}>
            <div className={styles.row}>
              <div className={styles.start}>
                <div className={styles.circle}>
                  <img src={CalendarIco} alt="date" className={styles.icon} />
                </div>
                <div className={styles.bold_text}>{t("Date")}</div>
              </div>
              <div className={styles.bold_text}>{formattedDate()}</div>
            </div>
            <div className={styles.row}>
              <div className={styles.start}>
                <div className={styles.circle}>
                  <img src={ClockIco} alt="time" className={styles.icon} />
                </div>
                <div className={styles.bold_text}>{t("Time")}</div>
              </div>
              <div className={styles.bold_text}>{formattedTime()}</div>
            </div>
            {bookSession.resourceLines?.map((line) => (
              <div className={styles.row} data-testid="booked-resource-row" key={line.section}>
                <div className={styles.start}>
                  <div className={styles.circle}>
                    <img src={LocationIco} alt="resource" className={styles.icon} />
                  </div>
                  <div className={styles.bold_text}>{line.section || t("Resource")}</div>
                </div>
                <div className={styles.bold_text}>{line.titles}</div>
              </div>
            ))}
          </div>
        </div>
      </InfoModal>

      <PurchaseSuccessModal
        isOpen={showPurchaseSuccessModal}
        subscriptionName={purchasedProductTitle}
        isSubscription={isSubscription}
        onClose={() => setShowPurchaseSuccessModal(false)}
        onViewPurchases={() => {
          setShowPurchaseSuccessModal(false);
          history.push("/account/athlete_subscriptions");
        }}
      />
    </div>
  );
}
